import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../redux/store";
import FormInput from "../UI/form-input/form-input.component";
import Button from "../UI/button/button.component";
import {
  ButtonColumnContainer,
  GreenHeader,
  GreenP,
  RedHeader,
  RedP,
} from "../../global.styles";
import useAutosizeTextArea from "../UI/textarea/useAutosizedTextArea";
import { StyledTextArea } from "../UI/textarea/textarea.component";
import { SpacerVertical } from "../UI/spacers/spacers.component";
import { createBookingStart } from "../../redux/bookings/bookings.slice";
import Spinner from "../UI/spinner/spinner.component";
import { sanitizeDriverNumber } from "../../util-functions";
import { BookingType } from "../../redux/bookings/bookings.types";
import RadioButton from "../UI/radio-buttons/radio-button";

type ConfirmBookingFormProps = {
  quote: BookingType;
};

const ConfirmBookingForm: FC<ConfirmBookingFormProps> = ({ quote }) => {
  const dispatch = useDispatch();

  const { bookingsError, bookingsLoading, bookingCreated } = useSelector(
    (state: RootState) => state.bookings
  );

  //Determine if it's a booking from  ugisPartner
  const isUgisPartnerBooking =
    quote.ugisPartnerDetails && quote.ugisPartnerDetails.active;
  //Ugis Partner bookings are private requests to Drivers 3 - Ugi
  //Please note that in some cases backend will transform it to 2 - Valdis
  const initialDriverNumber =
    quote.ugisPartnerDetails && quote.ugisPartnerDetails.active ? "3" : "";

  const [passengerName, setPassengerName] = useState("");
  const [passengerPhone, setPassengerPhone] = useState("");
  const [hasFavouriteDriver, setHasFavouriteDriver] = useState<boolean | null>(
    null
  );
  const [driverSelectFailed, setDriverSelectFailed] = useState(false);
  const [driverNumber, setDriverNumber] = useState(initialDriverNumber);
  const [driverNote, setDriverNote] = useState("");

  const [submitError, setSubmitError] = useState("");

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, driverNote);

  useEffect(() => {
    if (bookingCreated) {
      setPassengerName("");
      setPassengerPhone("");
      setDriverNumber("");
      setDriverNote("");
    }
  }, [bookingCreated]);

  const passengerNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setPassengerName(event.target.value);
  };
  const passengerPhoneChangeHandler = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setPassengerPhone(event.target.value);
  };
  const driverNumberChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const maxLength = 11;
    setDriverNumber(
      event.target.value.toUpperCase().replaceAll(" ", "").slice(0, maxLength)
    );
  };
  const driverNoteChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDriverNote(event.target.value);
  };

  const submitHandler = async () => {
    setSubmitError("");
    //check is passengers' name and phone number are provided
    if (!passengerName || !passengerPhone) {
      setSubmitError("Passengers phone or name is missing.");
      return;
    }
    //check if favourite driver option is selected
    if (hasFavouriteDriver === null && !isUgisPartnerBooking) {
      setDriverSelectFailed(true);
      return;
    }
    //sanitize Driver Number if there is one
    let sanitizedDriverNumber = "";
    if (typeof driverNumber === "string" && driverNumber.length > 0) {
      try {
        sanitizedDriverNumber = sanitizeDriverNumber(driverNumber);
      } catch (error) {
        if (error instanceof Error) {
          setSubmitError(error.message);
          return;
        } else {
          setSubmitError("Invalid Driver Number");
          return;
        }
      }
    }
    const bookingInfo = {
      passengerName,
      passengerPhone,
      driverNote,
      driverNumber: sanitizedDriverNumber,
    };
    dispatch(createBookingStart(bookingInfo));
  };

  return (
    <>
      {bookingsLoading ? (
        <Spinner />
      ) : (
        <>
          {submitError ? (
            <RedHeader>{submitError}</RedHeader>
          ) : bookingsError ? (
            <RedHeader>{bookingsError}</RedHeader>
          ) : (
            <GreenHeader>Almost done:</GreenHeader>
          )}
          <GreenP>
            Please provide the <b>name</b> and contact <b>phone number</b> for
            the Passenger. By doing so, You accept that it will be{" "}
            <b>shared with the Driver</b>.
          </GreenP>
          <FormInput
            label="Passenger's Name"
            id="passengerName"
            type="text"
            onChange={passengerNameChangeHandler}
            value={passengerName}
            required
          />
          <FormInput
            label="Passenger's Phone Number"
            id="passengerPhone"
            type="text"
            onChange={passengerPhoneChangeHandler}
            value={passengerPhone}
            inputMode="tel"
            required
          />
          <SpacerVertical $paddingRem={2.5}>
            <GreenP>
              Below, You can enter any additional notes or requests to Your
              Driver (optional):
            </GreenP>
            <br />
            <StyledTextArea
              id="review-text"
              onChange={driverNoteChangeHandler}
              placeholder="Add Note, flight number etc..."
              ref={textAreaRef}
              rows={1}
              value={driverNote}
            />
          </SpacerVertical>
          <br />
          <br />
          {isUgisPartnerBooking ? null : ( //Ugis Partner bookings are private requests to Drivers 3 - Ugi (in some cases 2 - Valdis)
            <>
              {driverSelectFailed ? (
                <RedP>* Do You have a Favoutite Driver?</RedP>
              ) : (
                <GreenP>Do You have a Favoutite Driver?</GreenP>
              )}
              <RadioButton
                label="Any Driver will do"
                id="any-driver"
                checked={hasFavouriteDriver === false}
                onChange={() => setHasFavouriteDriver(false)}
              />
              <RadioButton
                label="I have a Favourite Driver"
                id="favourite-driver"
                checked={hasFavouriteDriver === true}
                onChange={() => setHasFavouriteDriver(true)}
              />
              {hasFavouriteDriver && (
                <FormInput
                  label="Enter Driver Number"
                  id="driverNumber"
                  type="text"
                  onChange={driverNumberChangeHandler}
                  value={driverNumber}
                  inputMode="numeric"
                />
              )}
            </>
          )}
          {submitError && <RedHeader>{submitError}</RedHeader>}
          {bookingsError && <RedHeader>{bookingsError}</RedHeader>}
          <ButtonColumnContainer>
            <Button onClick={submitHandler}>Submit Booking</Button>
          </ButtonColumnContainer>
        </>
      )}
    </>
  );
};

export default ConfirmBookingForm;
